import React, { useEffect, useRef, useState } from "react";
import { Menu, Users, Download, LogIn, LogOut, RefreshCw, PanelLeft, FileText, Home } from "lucide-react";
import type { User } from "../firebase";

interface TopBarProps {
  profileName: string;
  onOpenProfiles: () => void;
  onExportWarband: () => void;
  onToggleLeftSidebar: () => void;
  leftSidebarOpen: boolean;
  user: User | null;
  firestoreReady: boolean;
  onGoogleSignIn: () => void;
  onSyncNow: () => void;
  onLogout: () => void;
  onOpenTemplates: () => void;
  onOpenWarband: () => void;
}

export const TopBar: React.FC<TopBarProps> = ({
  profileName,
  onOpenProfiles,
  onExportWarband,
  onToggleLeftSidebar,
  leftSidebarOpen,
  user,
  firestoreReady,
  onGoogleSignIn,
  onSyncNow,
  onLogout,
  onOpenTemplates,
  onOpenWarband,
}) => {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const isAnonymous = !!user?.isAnonymous;
  const isSignedIn = !!user && !isAnonymous;

  return (
    <div className="fixed top-0 inset-x-0 z-50 bg-[#1a1a1a] h-14">
      <div className="max-w-6xl mx-auto px-3 h-full flex items-center justify-between gap-3 text-sm text-[#E6E5D3]">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={onToggleLeftSidebar}
            className="bg-[#222] hover:bg-[#2a2a2a] text-[#E6E5D3] px-3 py-2 flex items-center gap-2"
            title={leftSidebarOpen ? "Hide sidebar" : "Show sidebar"}
          >
            <PanelLeft size={16} />
          </button>
          <button
            onClick={onOpenProfiles}
            className="text-[#E6E5D3] hover:text-white font-semibold uppercase tracking-wider truncate"
            title="Change Profile"
          >
            {profileName || "Select Profile"}
          </button>
        </div>

        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setOpen((v) => !v)}
            className="bg-[#222] hover:bg-[#2a2a2a] text-[#E6E5D3] px-3 py-2 flex items-center gap-2"
            title="Options"
          >
            <Menu size={16} />
            <span className="hidden sm:inline font-semibold uppercase tracking-wider text-sm">Options</span>
          </button>

          {open && (
            <div className="absolute right-0 mt-2 w-64 bg-[#1a1a1a] overflow-hidden">
              <button
                onClick={() => {
                  setOpen(false);
                  onExportWarband();
                }}
                className="w-full px-3 py-2 text-left hover:bg-[#222] flex items-center gap-2 text-[#E6E5D3]"
              >
                <Download size={16} />
                Export Warband Text
              </button>

              <button
                onClick={() => {
                  setOpen(false);
                  onOpenProfiles();
                }}
                className="w-full px-3 py-2 text-left hover:bg-[#222] flex items-center gap-2 text-[#E6E5D3]"
              >
                <Users size={16} />
                Change Profile
              </button>

              <button
                onClick={() => {
                  setOpen(false);
                  onOpenWarband();
                }}
                className="w-full px-3 py-2 text-left hover:bg-[#222] flex items-center gap-2 text-[#E6E5D3]"
              >
                <Home size={16} />
                Warband
              </button>

              <button
                onClick={() => {
                  setOpen(false);
                  onOpenTemplates();
                }}
                className="w-full px-3 py-2 text-left hover:bg-[#222] flex items-center gap-2 text-[#E6E5D3]"
              >
                <FileText size={16} />
                Templates
              </button>

              <div className="h-px bg-[#2a2a2a]" />

              {!isSignedIn && (
                <button
                  onClick={() => {
                    setOpen(false);
                    onGoogleSignIn();
                  }}
                  disabled={!firestoreReady}
                  className="w-full px-3 py-2 text-left hover:bg-[#222] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-[#E6E5D3]"
                >
                  <LogIn size={16} />
                  Sign in with Google
                </button>
              )}

              {isSignedIn && (
                <>
                  <button
                    onClick={() => {
                      setOpen(false);
                      onSyncNow();
                    }}
                    className="w-full px-3 py-2 text-left hover:bg-[#222] flex items-center gap-2 text-[#E6E5D3]"
                  >
                    <RefreshCw size={16} />
                    Sync Now
                  </button>
                  <button
                    onClick={() => {
                      setOpen(false);
                      onLogout();
                    }}
                    className="w-full px-3 py-2 text-left hover:bg-[#222] flex items-center gap-2 text-[#E6E5D3]"
                  >
                    <LogOut size={16} />
                    Logout
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

